/**
 * Deal Analyzer Page - Phase 2 (Compact Layout)
 * Professional-grade deal analysis with scenarios, sensitivity, and risk detection
 * Optimized to fit on one screen without scrolling
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Calculator,
  TrendUp,
  TrendDown,
  Warning,
  CheckCircle,
  Target,
  CurrencyEur,
  ChartLine,
  Shield,
  Lightning,
  Info,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { PageHelpBlock } from '../components/help';
import { pageExplanations } from '../content/helpContent';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import {
  formatCurrency,
  formatPercent,
  formatPricePerSqm,
  getRoiColorClass,
  getProfitColorClass,
} from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default input values
const defaultInputs = {
  acquisition: {
    purchase_price: 500000,
    notary_cost_pct: 7.5,
    agency_fee_pct: 0,
    additional_costs: 2000,
  },
  property: {
    surface_sqm: 60,
  },
  renovation: {
    renovation_cost_per_sqm: 1000,
    contingency_pct: 10,
    design_budget: 5000,
    project_management_fee: 5000,
  },
  financing: {
    equity: 300000,
    debt: 200000,
    interest_rate_annual: 4.5,
    holding_period_months: 10,
    financing_fees: 2000,
  },
  exit: {
    expected_sale_price_per_sqm: 12000,
    sales_fee_pct: 3.0,
    tax_pct: 0,
  },
  target_roi_pct: 20,
  market_avg_price_per_sqm: null,
};

// Compact input with inline label
const CompactInput = ({ label, value, onChange, suffix, className, ...props }) => (
  <div className={cn("flex items-center gap-2", className)}>
    <Label className="text-xs text-muted-foreground whitespace-nowrap min-w-[70px]">{label}</Label>
    <div className="relative flex-1">
      <Input
        value={value}
        onChange={onChange}
        className="h-7 text-sm font-tabular pr-6"
        {...props}
      />
      {suffix && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

// Compact slider with inline display
const CompactSlider = ({ label, value, onChange, min, max, step, suffix = '%' }) => (
  <div className="flex items-center gap-2">
    <Label className="text-xs text-muted-foreground whitespace-nowrap min-w-[70px]">{label}</Label>
    <Slider
      value={[value]}
      onValueChange={([v]) => onChange(v)}
      min={min}
      max={max}
      step={step}
      className="flex-1"
    />
    <span className="text-xs font-tabular w-12 text-right">{value}{suffix}</span>
  </div>
);

// Format helpers
const formatInputValue = (value) => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('fr-FR').format(value);
};

const parseInputValue = (value) => {
  return parseFloat(value.replace(/[^\d.-]/g, '')) || 0;
};

export const DealAnalyzer = () => {
  const [inputs, setInputs] = useState(defaultInputs);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateInput = useCallback((section, field, value) => {
    setInputs(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: parseFloat(value) || 0,
      },
    }));
  }, []);

  const analyzeDeall = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/analyzer/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inputs),
      });
      if (!response.ok) throw new Error('Analysis failed');
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [inputs]);

  // Auto-analyze on mount
  useEffect(() => {
    analyzeDeall();
  }, []);

  const roi = result?.profit?.roi_pct || 0;
  const profit = result?.profit?.net_profit || 0;
  const isPositive = profit > 0;

  return (
    <TooltipProvider>
      <div className="min-h-0 flex flex-col" data-testid="deal-analyzer">
        {/* Compact Header */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2 sm:gap-3">
            <Calculator size={20} className="text-primary sm:w-6 sm:h-6" />
            <h1 className="text-lg sm:text-xl font-bold">Analyseur Rapide</h1>
          </div>
          <Button 
            onClick={analyzeDeall} 
            disabled={loading}
            size="sm"
            className="bg-[#3A4A40] hover:bg-[#2E3B33]"
            data-testid="analyze-btn"
          >
            <Lightning size={16} className="mr-1" />
            {loading ? 'Analyse...' : 'Analyser'}
          </Button>
        </div>

        {/* Main Content - Responsive Grid */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-0 overflow-y-auto lg:overflow-visible">
          
          {/* Column 1: Inputs */}
          <div className="lg:col-span-3 space-y-3 lg:overflow-y-auto lg:pr-2">
            {/* Acquisition */}
            <div className="bg-card border border-border rounded-lg p-3">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                <CurrencyEur size={14} className="text-primary" />
                Acquisition
              </h3>
              <div className="space-y-2">
                <CompactInput
                  label="Prix"
                  value={formatInputValue(inputs.acquisition.purchase_price)}
                  onChange={(e) => updateInput('acquisition', 'purchase_price', parseInputValue(e.target.value))}
                  suffix="€"
                  data-testid="input-purchase-price"
                />
                <CompactSlider
                  label="Notaire"
                  value={inputs.acquisition.notary_cost_pct}
                  onChange={(v) => updateInput('acquisition', 'notary_cost_pct', v)}
                  min={5} max={10} step={0.5}
                />
                <CompactSlider
                  label="Agence"
                  value={inputs.acquisition.agency_fee_pct}
                  onChange={(v) => updateInput('acquisition', 'agency_fee_pct', v)}
                  min={0} max={5} step={0.5}
                />
              </div>
            </div>

            {/* Property & Renovation */}
            <div className="bg-card border border-border rounded-lg p-3">
              <h3 className="text-sm font-semibold mb-2">Bien & Rénovation</h3>
              <div className="space-y-2">
                <CompactInput
                  label="Surface"
                  value={inputs.property.surface_sqm}
                  onChange={(e) => updateInput('property', 'surface_sqm', e.target.value)}
                  suffix="m²"
                  type="number"
                  data-testid="input-surface"
                />
                <CompactInput
                  label="Réno/m²"
                  value={formatInputValue(inputs.renovation.renovation_cost_per_sqm)}
                  onChange={(e) => updateInput('renovation', 'renovation_cost_per_sqm', parseInputValue(e.target.value))}
                  suffix="€"
                  data-testid="input-reno-per-sqm"
                />
                <CompactSlider
                  label="Imprévu"
                  value={inputs.renovation.contingency_pct}
                  onChange={(v) => updateInput('renovation', 'contingency_pct', v)}
                  min={0} max={25} step={1}
                />
                <div className="grid grid-cols-2 gap-2">
                  <CompactInput
                    label="Design"
                    value={formatInputValue(inputs.renovation.design_budget)}
                    onChange={(e) => updateInput('renovation', 'design_budget', parseInputValue(e.target.value))}
                    className="flex-col items-start gap-0.5"
                  />
                  <CompactInput
                    label="MOE"
                    value={formatInputValue(inputs.renovation.project_management_fee)}
                    onChange={(e) => updateInput('renovation', 'project_management_fee', parseInputValue(e.target.value))}
                    className="flex-col items-start gap-0.5"
                  />
                </div>
              </div>
            </div>

            {/* Financing */}
            <div className="bg-card border border-border rounded-lg p-3">
              <h3 className="text-sm font-semibold mb-2">Financement</h3>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <CompactInput
                    label="Fonds Propres"
                    value={formatInputValue(inputs.financing.equity)}
                    onChange={(e) => updateInput('financing', 'equity', parseInputValue(e.target.value))}
                    className="flex-col items-start gap-0.5"
                  />
                  <CompactInput
                    label="Emprunt"
                    value={formatInputValue(inputs.financing.debt)}
                    onChange={(e) => updateInput('financing', 'debt', parseInputValue(e.target.value))}
                    className="flex-col items-start gap-0.5"
                  />
                </div>
                <CompactSlider
                  label="Taux"
                  value={inputs.financing.interest_rate_annual}
                  onChange={(v) => updateInput('financing', 'interest_rate_annual', v)}
                  min={0} max={10} step={0.1}
                />
                <CompactInput
                  label="Durée"
                  value={inputs.financing.holding_period_months}
                  onChange={(e) => updateInput('financing', 'holding_period_months', e.target.value)}
                  suffix="mois"
                  type="number"
                />
              </div>
            </div>

            {/* Exit */}
            <div className="bg-card border border-border rounded-lg p-3">
              <h3 className="text-sm font-semibold mb-2">Sortie</h3>
              <div className="space-y-2">
                <CompactInput
                  label="Vente/m²"
                  value={formatInputValue(inputs.exit.expected_sale_price_per_sqm)}
                  onChange={(e) => updateInput('exit', 'expected_sale_price_per_sqm', parseInputValue(e.target.value))}
                  suffix="€"
                  data-testid="input-exit-price"
                />
                <CompactSlider
                  label="Frais Vente"
                  value={inputs.exit.sales_fee_pct}
                  onChange={(v) => updateInput('exit', 'sales_fee_pct', v)}
                  min={0} max={6} step={0.5}
                />
                <CompactSlider
                  label="Cible Rendt"
                  value={inputs.target_roi_pct}
                  onChange={(v) => setInputs(prev => ({ ...prev, target_roi_pct: v }))}
                  min={5} max={40} step={1}
                />
              </div>
            </div>
          </div>

          {/* Column 2: Results */}
          <div className="lg:col-span-5 space-y-3 lg:overflow-y-auto">
            {result && (
              <>
                {/* Key Metrics Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className={cn(
                    "bg-card border rounded-lg p-2 sm:p-3 text-center",
                    isPositive ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"
                  )}>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Profit Net</p>
                    <p className={cn("text-base sm:text-lg font-bold", getProfitColorClass(profit))}>
                      {formatCurrency(profit)}
                    </p>
                  </div>
                  <div className={cn(
                    "bg-card border rounded-lg p-2 sm:p-3 text-center",
                    roi >= 15 ? "border-emerald-200 bg-emerald-50" : roi >= 10 ? "border-amber-200 bg-amber-50" : "border-red-200 bg-red-50"
                  )}>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Rendement</p>
                    <p className={cn("text-base sm:text-lg font-bold", getRoiColorClass(roi))}>
                      {formatPercent(roi)}
                    </p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-2 sm:p-3 text-center">
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Marge</p>
                    <p className="text-base sm:text-lg font-bold">
                      {formatPercent(result.profit?.gross_margin_pct || 0)}
                    </p>
                  </div>
                  <div className="bg-card border border-border rounded-lg p-2 sm:p-3 text-center">
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Seuil Rentab.</p>
                    <p className="text-base sm:text-lg font-bold text-primary">
                      {formatCurrency(result.break_even?.break_even_sale_price || 0)}
                    </p>
                  </div>
                </div>

                {/* Cost Breakdown & Bid Recommendations */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Cost Breakdown */}
                  <div className="bg-card border border-border rounded-lg p-3">
                    <h3 className="text-sm font-semibold mb-2">Détail des Coûts</h3>
                    <div className="space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Acquisition</span>
                        <span className="font-tabular">{formatCurrency(result.costs?.total_acquisition_cost || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Rénovation</span>
                        <span className="font-tabular">{formatCurrency(result.costs?.total_renovation_cost || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Financement</span>
                        <span className="font-tabular">{formatCurrency(result.costs?.total_financing_cost || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Frais de Sortie</span>
                        <span className="font-tabular">{formatCurrency(result.profit?.sales_fees || 0)}</span>
                      </div>
                      <div className="flex justify-between pt-1.5 border-t font-semibold">
                        <span>Coût Total</span>
                        <span className="font-tabular">{formatCurrency(result.costs?.total_project_cost || 0)}</span>
                      </div>
                      <div className="flex justify-between text-primary">
                        <span>Prix de Vente</span>
                        <span className="font-tabular font-semibold">{formatCurrency(result.profit?.exit_value || 0)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Bid Recommendations */}
                  <div className="bg-card border border-border rounded-lg p-3">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                      <Target size={14} className="text-primary" />
                      Recommandations d'Offre
                    </h3>
                    <div className="space-y-1.5 text-sm">
                      {result.bid && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Max</span>
                            <span className="font-tabular font-semibold text-amber-600">
                              {formatCurrency(result.bid.max_purchase_price)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Recommandée</span>
                            <span className="font-tabular font-semibold text-emerald-600">
                              {formatCurrency(result.bid.recommended_bid)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Agressive</span>
                            <span className="font-tabular">
                              {formatCurrency(result.bid.aggressive_bid)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-1.5 border-t">
                            <span className="text-muted-foreground">Limite</span>
                            <span className="font-tabular text-red-600">
                              {formatCurrency(result.bid.walk_away_price)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">vs. Actuel</span>
                            <Badge variant={result.bid.negotiation_room_pct < 0 ? "destructive" : "success"} className="text-xs">
                              {result.bid.negotiation_room_pct > 0 ? '+' : ''}
                              {result.bid.negotiation_room_pct?.toFixed(1)}%
                            </Badge>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Scenarios Comparison */}
                <div className="bg-card border border-border rounded-lg p-3">
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                    <ChartLine size={14} className="text-primary" />
                    Analyse de Scénarios
                  </h3>
                  {result.scenarios && (
                    <div className="grid grid-cols-3 gap-3">
                      {result.scenarios.map((scenario, idx) => {
                        const isBase = scenario.scenario_type === 'base_case';
                        const scenarioNames = {
                          'Conservative': 'Conservateur',
                          'Base Case': 'Cas de Base',
                          'Aggressive': 'Agressif'
                        };
                        const displayName = scenarioNames[scenario.name] || scenario.name;
                        return (
                          <div 
                            key={idx} 
                            className={cn(
                              "rounded-lg p-2 text-center",
                              isBase ? "bg-primary/10 border border-primary/30" : "bg-muted/50"
                            )}
                          >
                            <p className="text-xs font-medium mb-1">{displayName}</p>
                            <p className={cn(
                              "text-sm font-bold",
                              scenario.profit?.net_profit >= 0 ? "text-emerald-600" : "text-red-600"
                            )}>
                              {formatCurrency(scenario.profit?.net_profit || 0)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              ROI: {formatPercent(scenario.profit?.roi_pct || 0)}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            {!result && !loading && !error && (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Cliquez sur "Analyser" pour lancer l'analyse
              </div>
            )}
          </div>

          {/* Column 3: Risk & Sensitivity */}
          <div className="lg:col-span-4 space-y-3 lg:overflow-y-auto">
            {result && (
              <>
                {/* Risk Assessment */}
                <div className="bg-card border border-border rounded-lg p-3">
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                    <Shield size={14} className="text-primary" />
                    Évaluation des Risques
                  </h3>
                  {result.risk?.risk_flags && result.risk.risk_flags.length > 0 ? (
                    <div className="space-y-1.5">
                      {result.risk.risk_flags.slice(0, 4).map((flag, idx) => (
                        <div 
                          key={idx}
                          className={cn(
                            "flex items-start gap-2 p-2 rounded text-xs",
                            flag.severity === 'critical' ? "bg-red-50 text-red-700" :
                            flag.severity === 'warning' ? "bg-amber-50 text-amber-700" :
                            "bg-blue-50 text-blue-700"
                          )}
                        >
                          <Warning size={14} className="mt-0.5 flex-shrink-0" />
                          <span>{flag.message}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-emerald-600 text-sm">
                      <CheckCircle size={16} />
                      <span>{result.risk?.summary || 'Aucun risque majeur détecté'}</span>
                    </div>
                  )}
                </div>

                {/* Price Sensitivity */}
                <div className="bg-card border border-border rounded-lg p-3">
                  <h3 className="text-sm font-semibold mb-2">Sensibilité au Prix</h3>
                  {result.sensitivity?.sale_price_sensitivities && (
                    <div className="space-y-1">
                      {result.sensitivity.sale_price_sensitivities.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                          <span className={cn(
                            "w-10 text-right font-medium",
                            item.change_pct < 0 ? "text-red-600" : item.change_pct > 0 ? "text-emerald-600" : ""
                          )}>
                            {item.change_pct > 0 ? '+' : ''}{item.change_pct}%
                          </span>
                          <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={cn(
                                "h-full rounded-full",
                                item.new_roi_pct >= 15 ? "bg-emerald-500" : item.new_roi_pct >= 10 ? "bg-amber-500" : "bg-red-500"
                              )}
                              style={{ width: `${Math.min(100, Math.max(0, item.new_roi_pct * 3))}%` }}
                            />
                          </div>
                          <span className="w-16 text-right font-tabular">
                            ROI: {item.new_roi_pct?.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Key Metrics Summary */}
                <div className="bg-card border border-border rounded-lg p-3">
                  <h3 className="text-sm font-semibold mb-2">Résumé du Deal</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Prix/m² (achat)</span>
                      <span className="font-tabular">
                        {formatPricePerSqm(result.costs?.acquisition_cost_per_sqm || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Prix/m² (vente)</span>
                      <span className="font-tabular">
                        {formatPricePerSqm(result.profit?.exit_value_per_sqm || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Réno Totale</span>
                      <span className="font-tabular">{formatCurrency(result.costs?.total_renovation_cost || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Réno/m²</span>
                      <span className="font-tabular">
                        {formatCurrency(result.costs?.renovation_cost_per_sqm || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Coût de Portage</span>
                      <span className="font-tabular">{formatCurrency(result.costs?.total_financing_cost || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fonds Propres Requis</span>
                      <span className="font-tabular">{formatCurrency(inputs.financing.equity)}</span>
                    </div>
                  </div>
                </div>

                {/* Viability Verdict */}
                <div className={cn(
                  "rounded-lg p-3 text-center",
                  isPositive && roi >= 15 ? "bg-emerald-100 border border-emerald-300" :
                  isPositive && roi >= 10 ? "bg-amber-100 border border-amber-300" :
                  "bg-red-100 border border-red-300"
                )}>
                  <div className="flex items-center justify-center gap-2">
                    {isPositive && roi >= 15 ? (
                      <>
                        <CheckCircle size={20} className="text-emerald-600" />
                        <span className="font-semibold text-emerald-700">Deal Solide</span>
                      </>
                    ) : isPositive && roi >= 10 ? (
                      <>
                        <Warning size={20} className="text-amber-600" />
                        <span className="font-semibold text-amber-700">Deal Marginal</span>
                      </>
                    ) : (
                      <>
                        <TrendDown size={20} className="text-red-600" />
                        <span className="font-semibold text-red-700">
                          {isPositive ? 'Rendement Faible' : 'Perte Projetée'}
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-xs mt-1 text-muted-foreground">
                    {isPositive && roi >= 15 ? 'Dépasse le ROI cible, procédez avec confiance' :
                     isPositive && roi >= 10 ? 'En dessous de l\'objectif, négociez le prix' :
                     'Ne répond pas aux critères d\'investissement'}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Minimal Help Link */}
        <div className="mt-2 text-center">
          <a href="/help#quick-analyzer" className="text-xs text-muted-foreground hover:text-primary">
            Besoin d'aide ? Voir la documentation →
          </a>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default DealAnalyzer;
